import React from "react";
import { Tooltip } from "../../../components";
import { useTranslate } from "../../../helpers/intlHooks";

export interface ComparisonTableRowProps {
  benefitKey: string;
  heading: string | JSX.Element;
}

const PartACoinsuranceFM: React.FC = () => {
  const t = useTranslate();
  return (
    <>
      {t("medigap.help_drawer.part_a_coinsurance")}{" "}
      <Tooltip
        triggerText={t("medigap.help_drawer.hospital_costs")}
        title={t("medigap.help_drawer.hospital_costs")}
        inline
      >
        {t("medigap.help_drawer.hospital_costs_tooltip")}
      </Tooltip>
    </>
  );
};

const PartBExcessCostsFM: React.FC = () => {
  const t = useTranslate();

  return (
    <>
      {t("medigap.help_drawer.part_b_excess")}{" "}
      <Tooltip
        triggerText={t("medigap.help_drawer.excess_charges")}
        title={t("medigap.help_drawer.excess_charges")}
        inline
      >
        {t("medigap.help_drawer.excess_charges_tooltip")}
      </Tooltip>
    </>
  );
};

export const comparisonTableRows: ComparisonTableRowProps[] = [
  {
    benefitKey: "part_a_coinsurance",
    heading: <PartACoinsuranceFM />,
  },
  {
    benefitKey: "part_b_coinsurance",
    heading: "medigap.help_drawer.part_b_coinsurance",
  },
  {
    benefitKey: "first_blood",
    heading: "medigap.help_drawer.first_blood",
  },
  {
    benefitKey: "hospice",
    heading: "medigap.help_drawer.hospice",
  },
  {
    benefitKey: "nursing_facility",
    heading: "plan_terms.skilled_nursing_facility",
  },
  {
    benefitKey: "part_a_deductible",
    heading: "medigap.help_drawer.part_a_deductible",
  },
  {
    benefitKey: "part_b_deductible",
    heading: "medigap.help_drawer.part_b_deductible",
  },
  {
    benefitKey: "part_b_excess",
    heading: <PartBExcessCostsFM />,
  },
  {
    benefitKey: "foreign_travel",
    heading: "medigap.help_drawer.foreign_travel",
  },
];

export interface MedigapComparisonPlan {
  plan_name: string;
  part_a_coinsurance: boolean;
  part_b_coinsurance: boolean | string;
  first_blood: boolean | string;
  hospice: boolean | string;
  nursing_facility: boolean | string;
  part_a_deductible: boolean | string;
  part_b_deductible: boolean;
  part_b_excess: boolean;
  foreign_travel: boolean | string;
  out_of_pocket_limit?: string;
}

export const plans: MedigapComparisonPlan[] = [
  {
    plan_name: "A",
    part_a_coinsurance: true,
    part_b_coinsurance: true,
    first_blood: true,
    hospice: true,
    nursing_facility: false,
    part_a_deductible: false,
    part_b_deductible: false,
    part_b_excess: false,
    foreign_travel: false,
  },
  {
    plan_name: "B",
    part_a_coinsurance: true,
    part_b_coinsurance: true,
    first_blood: true,
    hospice: true,
    nursing_facility: false,
    part_a_deductible: true,
    part_b_deductible: false,
    part_b_excess: false,
    foreign_travel: false,
  },
  {
    plan_name: "C",
    part_a_coinsurance: true,
    part_b_coinsurance: true,
    first_blood: true,
    hospice: true,
    nursing_facility: true,
    part_a_deductible: true,
    part_b_deductible: true,
    part_b_excess: false,
    foreign_travel: "80%",
  },
  {
    plan_name: "D",
    part_a_coinsurance: true,
    part_b_coinsurance: true,
    first_blood: true,
    hospice: true,
    nursing_facility: true,
    part_a_deductible: true,
    part_b_deductible: false,
    part_b_excess: false,
    foreign_travel: "80%",
  },
  {
    plan_name: "F",
    part_a_coinsurance: true,
    part_b_coinsurance: true,
    first_blood: true,
    hospice: true,
    nursing_facility: true,
    part_a_deductible: true,
    part_b_deductible: true,
    part_b_excess: true,
    foreign_travel: "80%",
  },
  {
    plan_name: "G",
    part_a_coinsurance: true,
    part_b_coinsurance: true,
    first_blood: true,
    hospice: true,
    nursing_facility: true,
    part_a_deductible: true,
    part_b_deductible: false,
    part_b_excess: true,
    foreign_travel: "80%",
  },
  {
    plan_name: "K",
    part_a_coinsurance: true,
    part_b_coinsurance: "50%",
    first_blood: "50%",
    hospice: "50%",
    nursing_facility: "50%",
    part_a_deductible: "50%",
    part_b_deductible: false,
    part_b_excess: false,
    foreign_travel: false,
    out_of_pocket_limit: "$5,880",
  },
  {
    plan_name: "L",
    part_a_coinsurance: true,
    part_b_coinsurance: "75%",
    first_blood: "75%",
    hospice: "75%",
    nursing_facility: "75%",
    part_a_deductible: "75%",
    part_b_deductible: false,
    part_b_excess: false,
    foreign_travel: false,
    out_of_pocket_limit: "$2,940",
  },
  {
    plan_name: "M",
    part_a_coinsurance: true,
    part_b_coinsurance: true,
    first_blood: true,
    hospice: true,
    nursing_facility: true,
    part_a_deductible: "50%",
    part_b_deductible: false,
    part_b_excess: false,
    foreign_travel: "80%",
  },
  {
    plan_name: "N",
    part_a_coinsurance: true,
    part_b_coinsurance: true,
    first_blood: true,
    hospice: true,
    nursing_facility: true,
    part_a_deductible: true,
    part_b_deductible: false,
    part_b_excess: false,
    foreign_travel: "80%",
  },
];
